import { SearchOutlined } from "@ant-design/icons";
import { Drawer, Input, Table, Typography } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import FinishedSincePassedTable from "../FinishedSincePassedTable";
import { onFormatPatientAge } from "../../../../../../utils/ageFormat";

const { Text } = Typography;

// child_age_in_month adalah total bulan, sedangkan onFormatPatientAge
// menerima (tahun, total bulan) dan menampilkan sisa bulannya.
export const formatAgeInMonth = (ageInMonth) => {
  if (ageInMonth === null || ageInMonth === undefined) return "-";
  return onFormatPatientAge(Math.floor(ageInMonth / 12), ageInMonth);
};

// passed_at sudah waktu lokal klinik (Asia/Makassar), jangan dikonversi dari UTC.
export const formatPassedAt = (passedAt) => {
  if (!passedAt) return "-";
  const parsed = moment(passedAt, "YYYY-MM-DD HH:mm:ss");
  return parsed.isValid() ? parsed.format("HH:mm:ss") : "-";
};

export const formatPassedProgress = (queuesPassedCount, threshold) => {
  const passed = queuesPassedCount ?? 0;
  return threshold ? `${passed} / ${threshold}` : `${passed}`;
};

const PassedQueueDrawer = ({
  isShow,
  onClose,
  passedQueues,
  passedCount,
  threshold,
  isLoading = false,
}) => {
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    if (!isShow) setKeyword("");
  }, [isShow]);

  const queues = Array.isArray(passedQueues) ? passedQueues : [];
  const total = passedCount ?? queues.length;

  const filteredQueues = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    if (!search) return queues;
    return queues.filter((item) =>
      item?.child_name?.toLowerCase().includes(search),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedQueues, keyword]);

  const columns = [
    {
      title: "No Antri",
      dataIndex: "queue_number",
      key: "queue_number",
      width: 100,
      render: (text) => <span>{text ?? "-"}</span>,
    },
    {
      title: "Nama",
      dataIndex: "child_name",
      key: "child_name",
      render: (text) => <span>{text || "-"}</span>,
    },
    {
      title: "Umur",
      dataIndex: "child_age_in_month",
      key: "child_age_in_month",
      width: 140,
      render: (text) => <span>{formatAgeInMonth(text)}</span>,
    },
    {
      title: "Waktu Terlewat",
      dataIndex: "passed_at",
      key: "passed_at",
      width: 140,
      render: (text) => <span>{formatPassedAt(text)}</span>,
    },
    {
      title: "Progres",
      dataIndex: "queues_passed_count",
      key: "queues_passed_count",
      width: 100,
      render: (text) => <span>{formatPassedProgress(text, threshold)}</span>,
    },
  ];

  return (
    <Drawer
      title={`Antrian Terlewat (${total})`}
      open={isShow}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        {threshold
          ? `Progres adalah jumlah antrian yang selesai sejak antrian ini terlewat. Pada ${threshold} antrian, tiket dibatalkan dan dibuat ulang.`
          : "Progres adalah jumlah antrian yang selesai sejak antrian ini terlewat."}
      </Text>
      <Input
        placeholder="Cari nama pasien"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        suffix={<SearchOutlined />}
        style={{ marginBottom: 16 }}
      />
      <Table
        loading={isLoading}
        dataSource={filteredQueues}
        columns={columns}
        rowKey="queue_id"
        expandable={{
          rowExpandable: (record) => !!record?.finished_since_passed?.length,
          expandedRowRender: (record) => (
            <FinishedSincePassedTable items={record.finished_since_passed} />
          ),
        }}
        pagination={false}
        locale={{ emptyText: "Tidak ada antrian terlewat" }}
      />
    </Drawer>
  );
};

export default PassedQueueDrawer;
